import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Loader2, LogOut, Trash2, CalendarPlus, CalendarDays, Lock, Utensils, RefreshCw, Copy, Link2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "cm_admin_token";

const iso = (d) => format(d, "yyyy-MM-dd");
const parseISO = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const fmt = (s) => format(parseISO(s), "d MMM yyyy", { locale: pt });

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ bookings: [], blocks: [], gastro: [] });
  const [blockRange, setBlockRange] = useState(undefined);
  const [reason, setReason] = useState("");
  const [icalSources, setIcalSources] = useState([]);
  const [icalForm, setIcalForm] = useState({ name: "", url: "" });
  const [syncing, setSyncing] = useState(false);

  const authAxios = useCallback(
    () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await authAxios().get("/admin/bookings");
      setData(data);
      const { data: ical } = await authAxios().get("/admin/ical-sources");
      setIcalSources(ical.sources || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Sessão expirada. Inicie sessão novamente.");
      }
    }
  }, [token, authAxios]);

  useEffect(() => { load(); }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, creds);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success(`Bem-vindo, ${data.name}.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível iniciar sessão.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData({ bookings: [], blocks: [], gastro: [] });
  };

  const addBlock = async (e) => {
    e.preventDefault();
    if (!blockRange?.from || !blockRange?.to) {
      toast.error("Selecione o intervalo de datas a bloquear.");
      return;
    }
    try {
      await authAxios().post("/admin/blocks", {
        check_in: iso(blockRange.from),
        check_out: iso(blockRange.to),
        reason: reason || "Bloqueado",
      });
      toast.success("Datas bloqueadas.");
      setBlockRange(undefined);
      setReason("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao bloquear datas.");
    }
  };

  const del = async (kind, id) => {
    try {
      await authAxios().delete(`/admin/${kind}/${id}`);
      toast.success("Removido.");
      load();
    } catch {
      toast.error("Erro ao remover.");
    }
  };

  const addIcalSource = async (e) => {
    e.preventDefault();
    if (!icalForm.name.trim() || !icalForm.url.trim()) {
      toast.error("Indique o nome e o URL do feed iCal.");
      return;
    }
    try {
      await authAxios().post("/admin/ical-sources", icalForm);
      toast.success("Feed adicionado. A sincronizar...");
      setIcalForm({ name: "", url: "" });
      load();
      setTimeout(load, 4000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao adicionar feed.");
    }
  };

  const delIcalSource = async (id) => {
    try {
      await authAxios().delete(`/admin/ical-sources/${id}`);
      toast.success("Feed removido (bloqueios importados também foram removidos).");
      load();
    } catch {
      toast.error("Erro ao remover feed.");
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await authAxios().post("/admin/ical-sync");
      const ok = (data.results || []).filter((r) => r.ok);
      const fail = (data.results || []).filter((r) => !r.ok);
      if (fail.length === 0) {
        toast.success(`Sincronizado: ${ok.map((r) => `${r.name} (${r.events} eventos)`).join(" · ") || "sem feeds"}`);
      } else {
        toast.error(`Falha em: ${fail.map((r) => r.name).join(", ")}`);
      }
      load();
    } catch {
      toast.error("Erro ao sincronizar.");
    } finally {
      setSyncing(false);
    }
  };

  const exportUrl = `${API}/ical/export`;
  const copyExportUrl = async () => {
    try {
      await navigator.clipboard.writeText(exportUrl);
      toast.success("URL copiado. Cole na Booking/Airbnb (importar calendário).");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const confirmPayment = async (id) => {
    try {
      await authAxios().post(`/admin/bookings/${id}/confirm-payment`);
      toast.success("Pagamento confirmado. Email de confirmação enviado ao cliente.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao confirmar pagamento.");
    }
  };

  // ---- Login screen ----
  if (!token) {
    return (
      <div className="min-h-screen bg-forest flex items-center justify-center px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-md bg-cream rounded-sm p-10">
          <div className="flex items-center gap-2 text-terracotta mb-6"><Lock size={16} /><span className="overline">Área Reservada</span></div>
          <h1 className="font-display text-4xl text-forest">Casa <span className="italic">Montebello</span></h1>
          <p className="text-muted-foreground mt-2 mb-8">Painel de gestão de reservas.</p>
          <label className="block mb-4">
            <span className="block text-[0.7rem] tracking-[0.2em] uppercase text-muted-foreground mb-1">Email</span>
            <input data-testid="admin-email-input" type="email" required value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} className="w-full border-b border-border bg-transparent py-2 outline-none focus:border-forest" placeholder="admin@casamontebello.pt" />
          </label>
          <label className="block mb-8">
            <span className="block text-[0.7rem] tracking-[0.2em] uppercase text-muted-foreground mb-1">Password</span>
            <input data-testid="admin-password-input" type="password" required value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} className="w-full border-b border-border bg-transparent py-2 outline-none focus:border-forest" placeholder="••••••••" />
          </label>
          <button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full h-13 py-4 rounded-full bg-forest text-cream text-[0.8rem] tracking-[0.2em] uppercase font-medium hover:bg-[#1f2f21] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {loading ? <Loader2 size={16} className="animate-spin" /> : null} Entrar
          </button>
        </form>
      </div>
    );
  }

  // ---- Dashboard ----
  const upcoming = [...data.bookings].sort((a, b) => a.check_in.localeCompare(b.check_in));
  const gastro = [...(data.gastro || [])].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="min-h-screen bg-cream">
      <header className="sticky top-0 z-10 backdrop-blur-xl bg-cream/80 border-b border-border">
        <div className="mx-auto max-w-6xl px-6 h-20 flex items-center justify-between">
          <div className="font-display text-2xl text-forest">Casa <span className="italic">Montebello</span> · Gestão</div>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 text-sm text-forest hover:text-terracotta transition-colors">
            <LogOut size={16} /> Sair
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-12 grid lg:grid-cols-12 gap-10">
        {/* Bookings list */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-2 mb-6"><CalendarDays size={18} className="text-terracotta" /><h2 className="font-display text-3xl text-forest">Reservas de Alojamento ({upcoming.length})</h2></div>
          <div className="space-y-3" data-testid="admin-bookings-list">
            {upcoming.length === 0 && <p className="text-muted-foreground">Ainda não há reservas de alojamento.</p>}
            {upcoming.map((b) => (
              <div key={b.id} data-testid={`admin-booking-${b.id}`} className="bg-white border border-border rounded-sm p-5 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full bg-forest text-cream text-[0.6rem] tracking-[0.14em] uppercase">Alojamento</span>
                    {b.payment_status === "paid" && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 text-[0.6rem] tracking-[0.14em] uppercase">Pago</span>}
                    {b.payment_status === "pending" && b.status === "pending_payment" && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[0.6rem] tracking-[0.14em] uppercase">A aguardar pagamento</span>}
                    {b.payment_status === "pay_at_property" && <span className="px-2 py-0.5 rounded-full bg-[#EFEBE0] text-forest text-[0.6rem] tracking-[0.14em] uppercase">Paga no alojamento</span>}
                    {b.status === "cancelled" && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-900 text-[0.6rem] tracking-[0.14em] uppercase">Cancelada</span>}
                  </div>
                  <div className="font-medium text-forest">{b.guest_name} <span className="text-muted-foreground font-normal">· {b.guests_count || b.guests || "—"} hóspedes</span></div>
                  <div className="text-sm text-terracotta mt-1 capitalize">{fmt(b.check_in)} → {fmt(b.check_out)}</div>
                  {b.price_total != null && (
                    <div className="text-sm text-forest mt-1" data-testid={`admin-booking-price-${b.id}`}>
                      {b.payment_status === "paid" ? `${(b.price_paid ?? b.price_due ?? b.price_total).toFixed(2)} € pagos` : `${(b.price_due ?? b.price_total).toFixed(2)} €`}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground mt-1">{b.guest_email}{b.phone ? ` · ${b.phone}` : ""}</div>
                </div>
                <div className="flex flex-col items-end gap-3 shrink-0">
                  {b.status === "pending_payment" && (
                    <button
                      onClick={() => confirmPayment(b.id)}
                      data-testid={`admin-confirm-payment-${b.id}`}
                      className="px-4 h-9 rounded-full bg-forest text-cream text-[0.62rem] tracking-[0.14em] uppercase font-medium hover:bg-[#1f2f21] transition-colors"
                    >
                      Confirmar pagamento
                    </button>
                  )}
                  <button onClick={() => del("bookings", b.id)} data-testid={`admin-delete-booking-${b.id}`} className="text-muted-foreground hover:text-destructive transition-colors" title="Cancelar reserva"><Trash2 size={18} /></button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 mb-6 mt-12"><Utensils size={18} className="text-terracotta" /><h2 className="font-display text-3xl text-forest">Pedidos de Eventos ({gastro.length})</h2></div>
          <div className="space-y-3" data-testid="admin-gastro-list">
            {gastro.length === 0 && <p className="text-muted-foreground">Ainda não há pedidos de eventos privados.</p>}
            {gastro.map((g) => (
              <div key={g.id} data-testid={`admin-gastro-${g.id}`} className="bg-white border border-border rounded-sm p-5 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 rounded-full bg-terracotta text-cream text-[0.6rem] tracking-[0.14em] uppercase">Eventos Privados</span>
                    {g.status === "pending" && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[0.6rem] tracking-[0.14em] uppercase">Pendente</span>}
                  </div>
                  <div className="font-medium text-forest">{g.guest_name} <span className="text-muted-foreground font-normal">· {g.party_size} pessoa(s){g.event_type ? ` · ${g.event_type}` : ""}</span></div>
                  <div className="text-sm text-terracotta mt-1 capitalize">{fmt(g.date)}{g.shift ? ` · ${g.shift}` : ""}{g.time ? ` · ${g.time}` : ""}</div>
                  <div className="text-xs text-muted-foreground mt-1">{g.guest_email}{g.phone ? ` · ${g.phone}` : ""}</div>
                  {(g.cook_service || g.catering || g.decoration || g.cleaning || g.organization) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {[g.cook_service, g.catering, g.decoration, g.cleaning, g.organization].filter(Boolean).map((v) => (
                        <span key={v} className="text-[0.65rem] px-2 py-0.5 rounded-full bg-[#EFEBE0] text-forest">{v}</span>
                      ))}
                    </div>
                  )}
                  {g.extras && g.extras.length > 0 && (
                    <div className="mt-1.5 text-xs text-muted-foreground">Extras: {g.extras.join(", ")}</div>
                  )}
                  {g.message && <div className="mt-2 text-xs text-muted-foreground italic">"{g.message}"</div>}
                </div>
                <button onClick={() => del("gastro-bookings", g.id)} data-testid={`admin-delete-gastro-${g.id}`} className="text-muted-foreground hover:text-destructive transition-colors shrink-0" title="Remover pedido"><Trash2 size={18} /></button>
              </div>
            ))}
          </div>

          {data.blocks.length > 0 && (
            <>
              <h3 className="font-display text-2xl text-forest mt-10 mb-4">Datas bloqueadas</h3>
              <div className="space-y-3" data-testid="admin-blocks-list">
                {data.blocks.map((bl) => (
                  <div key={bl.id} className="bg-[#EFEBE0] border border-border rounded-sm p-4 flex items-center justify-between">
                    <div>
                      <div className="text-sm text-forest capitalize">{fmt(bl.check_in)} → {fmt(bl.check_out)}</div>
                      <div className="text-xs text-muted-foreground">{bl.reason}</div>
                    </div>
                    <button onClick={() => del("blocks", bl.id)} className="text-muted-foreground hover:text-destructive transition-colors" title="Remover bloqueio"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Block dates */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-2 mb-6"><CalendarPlus size={18} className="text-terracotta" /><h2 className="font-display text-3xl text-forest">Bloquear datas</h2></div>
          <form onSubmit={addBlock} className="bg-white border border-border rounded-sm p-6">
            <p className="text-sm text-muted-foreground mb-4">Selecione um intervalo para bloquear manualmente (ex.: manutenção, uso pessoal).</p>
            <Calendar
              mode="range"
              selected={blockRange}
              onSelect={setBlockRange}
              numberOfMonths={1}
              locale={pt}
              data-testid="admin-block-calendar"
              className="p-0"
              classNames={{
                day_selected: "bg-forest text-cream hover:bg-forest hover:text-cream",
                day_range_middle: "aria-selected:bg-[#EFEBE0] aria-selected:text-forest",
                day: "h-9 w-9 p-0 font-normal rounded-md hover:bg-[#EFEBE0]",
                caption_label: "text-sm font-medium capitalize text-forest font-display text-lg",
              }}
            />
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (opcional)" className="mt-4 w-full border-b border-border bg-transparent py-2 outline-none focus:border-forest text-sm" data-testid="admin-block-reason" />
            <button type="submit" data-testid="admin-block-submit" className="mt-6 w-full h-12 rounded-full bg-forest text-cream text-[0.78rem] tracking-[0.2em] uppercase font-medium hover:bg-[#1f2f21] transition-colors">Bloquear</button>
          </form>
        </div>
      </div>

      {/* iCal sync */}
      <div className="mx-auto max-w-6xl px-6 pb-16">
        <div className="flex items-center gap-2 mb-6"><RefreshCw size={18} className="text-terracotta" /><h2 className="font-display text-3xl text-forest">Sincronização de Calendários (iCal)</h2></div>
        <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-ical-section">
          {/* Importar */}
          <div className="bg-white border border-border rounded-sm p-6">
            <h3 className="font-display text-xl text-forest mb-1">Importar (Booking / Airbnb → site)</h3>
            <p className="text-sm text-muted-foreground mb-5">As datas reservadas nestas plataformas ficam automaticamente bloqueadas no calendário do site. Sincroniza a cada 30 minutos.</p>
            <div className="space-y-3 mb-5" data-testid="admin-ical-sources-list">
              {icalSources.length === 0 && <p className="text-muted-foreground text-sm">Nenhum feed configurado.</p>}
              {icalSources.map((s) => (
                <div key={s.id} data-testid={`admin-ical-source-${s.id}`} className="border border-border rounded-sm p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-forest flex items-center gap-2"><Link2 size={14} className="text-terracotta shrink-0" />{s.name}</div>
                    <div className="text-xs text-muted-foreground truncate mt-0.5">{s.url}</div>
                    <div className="text-xs mt-1.5">
                      {s.last_error ? (
                        <span className="text-destructive" data-testid={`admin-ical-error-${s.id}`}>Erro: {s.last_error}</span>
                      ) : s.last_synced ? (
                        <span className="text-forest/70">Última sincronização: {format(new Date(s.last_synced), "d MMM yyyy, HH:mm", { locale: pt })} · {s.events_count ?? 0} eventos</span>
                      ) : (
                        <span className="text-muted-foreground">Ainda não sincronizado</span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => delIcalSource(s.id)} data-testid={`admin-ical-delete-${s.id}`} className="text-muted-foreground hover:text-destructive transition-colors shrink-0" title="Remover feed"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <form onSubmit={addIcalSource} className="flex flex-col sm:flex-row gap-3">
              <input value={icalForm.name} onChange={(e) => setIcalForm({ ...icalForm, name: e.target.value })} placeholder="Nome (ex.: Booking.com)" className="sm:w-44 border-b border-border bg-transparent py-2 outline-none focus:border-forest text-sm" data-testid="admin-ical-name-input" />
              <input value={icalForm.url} onChange={(e) => setIcalForm({ ...icalForm, url: e.target.value })} placeholder="URL do feed iCal (.ics)" className="flex-1 border-b border-border bg-transparent py-2 outline-none focus:border-forest text-sm" data-testid="admin-ical-url-input" />
              <button type="submit" data-testid="admin-ical-add-btn" className="h-11 px-6 rounded-full bg-forest text-cream text-[0.72rem] tracking-[0.18em] uppercase font-medium hover:bg-[#1f2f21] transition-colors shrink-0">Adicionar</button>
            </form>
            <button onClick={syncNow} disabled={syncing} data-testid="admin-ical-sync-btn" className="mt-5 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-terracotta text-cream text-[0.72rem] tracking-[0.18em] uppercase font-medium hover:brightness-95 transition-all disabled:opacity-60">
              {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sincronizar agora
            </button>
          </div>
          {/* Exportar */}
          <div className="bg-white border border-border rounded-sm p-6">
            <h3 className="font-display text-xl text-forest mb-1">Exportar (site → Booking / Airbnb)</h3>
            <p className="text-sm text-muted-foreground mb-5">Cole este URL na Booking.com e na Airbnb ("Importar calendário") para que as reservas feitas no site bloqueiem essas plataformas.</p>
            <div className="flex items-center gap-2 bg-[#EFEBE0] border border-border rounded-sm p-3">
              <code className="flex-1 text-xs text-forest truncate" data-testid="admin-ical-export-url">{exportUrl}</code>
              <button onClick={copyExportUrl} data-testid="admin-ical-copy-btn" className="inline-flex items-center gap-1.5 text-forest hover:text-terracotta transition-colors shrink-0 text-xs uppercase tracking-[0.14em] font-medium"><Copy size={14} /> Copiar</button>
            </div>
            <p className="text-xs text-muted-foreground mt-4">Nota: as plataformas externas atualizam o calendário importado periodicamente (pode demorar algumas horas, conforme cada plataforma).</p>
          </div>
        </div>
      </div>
    </div>
  );
}
